import React from 'react';

import { Box, Container } from '@mui/material';
import Typography from '@mui/material/Typography';
import cx from 'classnames';
import parse from 'html-react-parser';

import SafeLink from '../../blocks/Header/components/SafeLink/SafeLink';
import { BREAKPOINTS_VALUES, useIsWidthUp } from '../../theme';
import { LazyLoadImage } from '../LazyloadImage';
import { CustomButton } from '../UI/CustomButton';
import useStyles from './styles';

const TEXT = 'text';
const IMAGE = 'image';

//object with different styling for single, first and second image
const IMAGE_DATA = {
  SINGLE: {
    wrapperClass: 'images-text-block__image-wrap',
    srcset: [],
    sizes: '',
  },
  FIRST: {
    wrapperClass: 'images-text-block__image-wrap',
    srcset: [{ width: 584 }, { width: 350 }],
    sizes: `(max-width:${BREAKPOINTS_VALUES['lg']}px) 584px, 350px`,
  },
  SECOND: {
    wrapperClass: 'images-text-block__second-image-wrap',
    srcset: [{ width: 420 }, { width: 343 }],
    sizes: `(max-width:${BREAKPOINTS_VALUES['lg']}px) 420px, 343px`,
  },
};

export const Text = ({ classes, heading, isImageAndTextBlock, text, buttonText, buttonUrl }) => {
  return (
    <Box className={cx(classes.textContainer, 'images-text-block__text-container')}>
      <Box className={cx(classes.textWrapper, 'images-text-block__text-wrapper')}>
        <Typography
          variant="h2"
          component="h2"
          className={cx(
            classes.itemHeading,
            'images-text-block__item-heading',
            isImageAndTextBlock && classes.leftAlign,
          )}
        >
          {heading}
        </Typography>
        <div className={cx(classes.itemText, 'images-text-block__item-text')}>{parse(text)}</div>
        {Boolean(buttonUrl && buttonText) && (
          <SafeLink to={buttonUrl}>
            <CustomButton btnClass={classes.btn}>{buttonText}</CustomButton>
          </SafeLink>
        )}
      </Box>
    </Box>
  );
};

const getImageData = (isSingle: boolean, isFirst: boolean) => {
  if (isSingle) return IMAGE_DATA.SINGLE;
  else if (isFirst) return IMAGE_DATA.FIRST;
  else return IMAGE_DATA.SECOND;
};

export const Image = ({
  classes,
  image,
  mobileImage = '',
  alt,
  isSingle = false,
  isFirst = false,
  isBgImage = false,
}) => {
  const imageData = getImageData(isSingle, isFirst);
  const className = isSingle
    ? classes.singleImageWrap
    : isFirst
    ? classes.firstImageWrap
    : classes.secondImageWrap;

  const isMobile = !useIsWidthUp('md');

  return (
    <div className={cx(className, imageData.wrapperClass)}>
      {isBgImage ? (
        <div className={classes.imagePreloaderWrap}>
          <LazyLoadImage
            imageWrapperClass={classes.imgGradientWrapper}
            path={isMobile && mobileImage ? mobileImage : image}
            alt={alt}
            draggable={false}
            srcset={imageData.srcset}
            sizes={imageData.sizes}
          />
        </div>
      ) : (
        <LazyLoadImage
          imageWrapperClass={classes.imagePreloaderWrap}
          path={isMobile && mobileImage ? mobileImage : image}
          alt={alt}
          draggable={false}
          srcset={imageData.srcset}
          sizes={imageData.sizes}
        />
      )}
    </div>
  );
};

const ImageTextArrayBlock = ({ data }) => {
  const background = data?.background;
  const shouldStartFromImage = data?.shouldStartFromImage;
  const itemsToFilter = data?.items;

  function* filter(array, condition, maxSize) {
    if (!maxSize || maxSize > array.length) {
      maxSize = array.length;
    }
    let count = 0;
    let i = 0;
    while (count < maxSize && i < array.length) {
      if (condition(array[i])) {
        yield array[i];
        count++;
      }
      i++;
    }
  }

  const texts = Array.from(filter(itemsToFilter, (i) => i.type === TEXT, 1));
  const images = Array.from(filter(itemsToFilter, (i) => i.type === IMAGE, 2));
  const imagesLength = images?.length;
  const items = imagesLength === 2 ? [images[0], ...texts, images[1]] : [...images, ...texts];
  const hasBackgroundImage = items.length === 2 && images?.[0]?.is_background;
  const classes = useStyles(background, shouldStartFromImage)();

  const heading = typeof data?.heading === 'string' ? data?.heading : data?.heading?.en;

  if (!items || items.length === 0) return null;
  const isImageAndTextBlock = !!(
    items.find((item) => item.type === IMAGE) && items.find((item) => item.type === TEXT)
  );

  return (
    <Box
      className={cx(
        classes.wrapper,
        hasBackgroundImage && classes.wrapperWithBgImage,
        background === 'Dark' ? 'dark-block' : 'light-block',
        'images-text-block',
        hasBackgroundImage && 'images-text-block__with-bg-image',
      )}
    >
      <Container className={cx(classes.container, imagesLength === 1 && classes.wideContainer)}>
        {!hasBackgroundImage && (
          <Typography
            variant="h2"
            component="h2"
            align="center"
            className={cx(classes.heading, 'images-text-block__heading')}
          >
            {heading}
          </Typography>
        )}
        <div className={classes.contentWrapper}>
          <div className={cx(classes.content, !shouldStartFromImage && classes.odd)}>
            {items &&
              items.map((item, index) => {
                const type = item?.type;
                return (
                  <React.Fragment key={index}>
                    {type === IMAGE && (
                      <Image
                        classes={classes}
                        image={item?.image}
                        mobileImage={item?.mobile_image}
                        alt={item?.alt}
                        isSingle={imagesLength === 1}
                        isFirst={imagesLength === 2 && index === 0}
                        isBgImage={hasBackgroundImage}
                      />
                    )}
                    {type === TEXT && (
                      <Text
                        classes={classes}
                        heading={heading}
                        text={item?.text}
                        isImageAndTextBlock={isImageAndTextBlock}
                        buttonText={item?.button_text}
                        buttonUrl={item?.button_url}
                      />
                    )}
                  </React.Fragment>
                );
              })}
          </div>
        </div>
      </Container>
    </Box>
  );
};

export default ImageTextArrayBlock;
